import { useState } from "react";
import { X, Star, Sparkles, Mail, Phone } from "lucide-react";
import { errorToast, successToast } from "../../utils/toasters";
import { claimBusiness } from "../../services/business";

export default function GallyoInsiderModal({
  business: businessDetail,
  isOpen,
  onClose
}) {
  const [formData, setFormData] = useState({
    email: "",
    phone: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = e => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async e => {
    e.preventDefault();

    if (!formData.email || !formData.phone) {
      errorToast("Please fill in all fields");
      return;
    }

    setIsSubmitting(true);

    try {
      const geohash = localStorage.getItem("geohash") || "9v6m";
      const googleApiKey = process.env.REACT_APP_GOOGLEMAP_API;

      const payload = {
        business: {
          businessname: businessDetail.name,
          businessaddress1: businessDetail.formatted_address,
          businesslocationmap: businessDetail.geometry.location,
          businessimageurl: businessDetail.photos
            ? `https://maps.googleapis.com/maps/api/place/photo?maxwidth=400&photo_reference=${
                businessDetail.photos[0].photo_reference
              }&key=${googleApiKey}`
            : null,
          status: "Unverified via Google",
          is_registered: businessDetail.is_registered,
          google_place_id: businessDetail.place_id,
          geohash: geohash,
          business_id: businessDetail.business_id || null
        },
        email: formData.email,
        phoneno: formData.phone
      };
      console.log("Gallyo Insider registration:", formData);

      await claimBusiness(payload);
      successToast(
        "Thanks for your interest! We'll be in touch soon to discuss exclusive opportunities."
      );
      handleClose();
    } catch (error) {
      console.error("Error registering for Gallyo Insider:", error);
      errorToast("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({ email: "", phone: "" });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="relative bg-gradient-to-r from-purple-600 to-pink-600 p-6 text-white">
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 p-1 h-8 w-8 rounded-md hover:bg-white hover:bg-opacity-20 transition-colors flex items-center justify-center"
          >
            <X className="h-4 w-4" />
          </button>

          <div className="flex items-center space-x-2 mb-2">
            <Sparkles className="h-6 w-6 text-yellow-300" />
            <h2
              className="text-xl font-bold"
              style={{ fontFamily: "Fraunces, serif" }}
            >
              Become a Gallyo Insider!
            </h2>
          </div>

          <p
            className="text-purple-100 text-sm"
            style={{ fontFamily: "Afacad, sans-serif" }}
          >
            Join our exclusive community of local business enthusiasts
          </p>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-4">
            {/* Benefits */}
            <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-4 rounded-lg">
              <h3
                className="font-semibold text-gray-800 mb-2 flex items-center"
                style={{ fontFamily: "Fraunces, serif" }}
              >
                <Star className="h-4 w-4 text-purple-600 mr-1" />
                Exclusive Perks
              </h3>
              <ul
                className="text-sm text-gray-600 space-y-1"
                style={{ fontFamily: "Afacad, sans-serif" }}
              >
                <li>• Owner gains access to "Manage My Business" dashboard:</li>
                <li className="ml-4">• Edit details</li>
                <li className="ml-4">• Post updates/promotions</li>
                <li className="ml-4">• Add photos</li>
              </ul>
            </div>

            <div
              className="text-center text-gray-600 text-sm"
              style={{ fontFamily: "Afacad, sans-serif" }}
            >
              <p className="mb-4">
                Our team will reach out to discuss how you can become part of
                our insider community and unlock exclusive benefits!
              </p>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label
                  className="block text-sm font-medium text-gray-700 mb-1"
                  style={{ fontFamily: "Afacad, sans-serif" }}
                >
                  Email Address
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    style={{ fontFamily: "Afacad, sans-serif" }}
                    placeholder="your@email.com"
                    required
                  />
                </div>
              </div>

              <div>
                <label
                  className="block text-sm font-medium text-gray-700 mb-1"
                  style={{ fontFamily: "Afacad, sans-serif" }}
                >
                  Phone Number
                </label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    style={{ fontFamily: "Afacad, sans-serif" }}
                    placeholder="(555) 123-4567"
                    required
                  />
                </div>
              </div>

              <div
                className="text-xs text-gray-500 text-center"
                style={{ fontFamily: "Afacad, sans-serif" }}
              >
                By submitting, you agree to be contacted by Gallyo about insider
                opportunities
              </div>

              <div className="flex space-x-3 pt-2">
                <button
                  type="button"
                  onClick={handleClose}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
                  style={{ fontFamily: "Afacad, sans-serif" }}
                >
                  Maybe Later
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-md hover:from-purple-700 hover:to-pink-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ fontFamily: "Afacad, sans-serif" }}
                >
                  {isSubmitting ? "Submitting..." : "Join Now!"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
